import {
  WEBSOCKET_JSON_DISCRIMINATORS,
  type WebSocketMessageSample,
} from './websocket-endpoint.util';

export const DEFAULT_WEBSOCKET_METHOD_SAMPLE_CAP = 2;

export type WebSocketMethodSample = {
  direction: WebSocketMessageSample['direction'];
  data: string;
};

export type WebSocketMethodInventoryEntry = {
  method: string;
  sampleCount: number;
  samples: WebSocketMethodSample[];
};

const UNTYPED_JSON_METHOD = '(json)';
const BINARY_METHOD = '(binary)';
const TEXT_METHOD = '(text)';

export function inventoryWebSocketMethods({
  messages,
  maxSamplesPerMethod = DEFAULT_WEBSOCKET_METHOD_SAMPLE_CAP,
}: {
  messages: readonly WebSocketMessageSample[];
  maxSamplesPerMethod?: number;
}): WebSocketMethodInventoryEntry[] {
  const sampleCap = Math.max(1, Math.floor(maxSamplesPerMethod));
  const grouped = new Map<
    string,
    { sampleCount: number; samples: WebSocketMethodSample[] }
  >();

  for (const message of messages) {
    const method = methodFromWebSocketMessage(message);

    if (!method) {
      continue;
    }

    const existing = grouped.get(method) ?? { sampleCount: 0, samples: [] };

    existing.sampleCount += 1;
    if (existing.samples.length < sampleCap) {
      existing.samples.push({
        direction: message.direction,
        data: message.data,
      });
    }

    grouped.set(method, existing);
  }

  return [...grouped.entries()].map(([method, entry]) => ({
    method,
    sampleCount: entry.sampleCount,
    samples: entry.samples,
  }));
}

export function webSocketMessagesIncludeJsonRpc(
  messages: readonly WebSocketMessageSample[],
): boolean {
  return messages.some((message) => {
    try {
      const parsed: unknown = JSON.parse(message.data);

      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return false;
      }

      const record = parsed as Record<string, unknown>;

      return (
        typeof record.jsonrpc === 'string' &&
        typeof record.method === 'string' &&
        Boolean(record.method.trim())
      );
    } catch {
      return false;
    }
  });
}

export function methodFromWebSocketMessage(
  message: WebSocketMessageSample,
): string | null {
  if (message.opcode === 'binary') {
    return BINARY_METHOD;
  }

  const trimmed = message.data.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);

    return methodFromJsonPayload(parsed);
  } catch {
    return methodFromTextStartLine(trimmed);
  }
}

function methodFromJsonPayload(parsed: unknown): string {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return UNTYPED_JSON_METHOD;
  }

  const record = parsed as Record<string, unknown>;

  for (const key of WEBSOCKET_JSON_DISCRIMINATORS) {
    const discriminator = record[key];

    if (typeof discriminator === 'string' && discriminator.trim()) {
      return discriminator.trim();
    }
  }

  return UNTYPED_JSON_METHOD;
}

function methodFromTextStartLine(data: string): string {
  const startLine = data.split(/\r?\n/, 1)[0]?.trim() ?? '';

  if (!startLine) {
    return TEXT_METHOD;
  }

  const versionStatus = startLine.match(/^(\S+\/\d+(?:\.\d+)?)\s+(\d{3})\b/);

  if (versionStatus) {
    return `${versionStatus[1]} ${versionStatus[2]}`;
  }

  return startLine.split(/\s+/, 1)[0] || TEXT_METHOD;
}
